import z from 'zod';

import {isNilOrEmpty} from 'ramda-adjunct';

import {testDriveStateSchema} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {BATTERY_CHARGE_MAX, BATTERY_CHARGE_MIN, castTimeToDateISOString} from '@dms/shared';

import {parseDate} from 'shared/web';

export const TestDriveDialogSchema = z
  .object({
    userId: z.string(),
    state: testDriveStateSchema,
    note: z.string().nullable(),
    scheduledAtDate: z.string(),
    scheduledAtTime: z.string(),
    fuelBefore: z.number().nullable().optional(),
    fuelAfter: z.number().nullable().optional(),
    batteryChargeBefore: z
      .number()
      .int()
      .min(BATTERY_CHARGE_MIN)
      .max(BATTERY_CHARGE_MAX)
      .nullable()
      .optional(),
    batteryChargeAfter: z
      .number()
      .int()
      .min(BATTERY_CHARGE_MIN)
      .max(BATTERY_CHARGE_MAX)
      .nullable()
      .optional(),
    mileageBefore: z.coerce.number().nullable().optional(),
    mileageAfter: z.coerce.number().nullable().optional(),
    returnedAtDate: z.string().nullable().optional(),
    returnedAtTime: z.string().nullable().optional(),
    vehicleId: z.string().nullable().optional(),
    internalNote: z.string().nullable().optional(),
    customerFeedback: z.string().nullable().optional(),
    customerId: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    customerDriversLicence: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.returnedAtDate && data.returnedAtTime) {
      const scheduledAtIso = castTimeToDateISOString(data.scheduledAtDate, data.scheduledAtTime);
      const returnedAtIso = castTimeToDateISOString(data.returnedAtDate, data.returnedAtTime);

      const scheduledAt = parseDate(scheduledAtIso);
      const returnedAt = parseDate(returnedAtIso);

      if (scheduledAt && returnedAt && returnedAt.getTime() < scheduledAt.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.testDrive.errors.returnDateInvalid'),
          path: ['returnedAtDate'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.testDrive.errors.returnDateInvalid'),
          path: ['returnedAtTime'],
        });
      }
    }
  });

export const TestDriveCreateDialogSchema = TestDriveDialogSchema.superRefine((data, ctx) => {
  (['vehicleId', 'customerId'] as const)
    .filter((field) => isNilOrEmpty(data[field]))
    .forEach((field) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.validations.fieldIsRequired'),
        path: [field],
      })
    );
});

export type TestDriveDialogSchemaType = z.infer<typeof TestDriveDialogSchema>;
